// Pure column-visibility helpers. No React, no `@/` imports: tested with node --experimental-strip-types.

function ordered(all: readonly string[], keep: ReadonlySet<string>): string[] {
  return all.filter((k) => keep.has(k));
}

/**
 * Parses a stored JSON array of column keys. Keeps only keys in `all` (in `all`'s order, deduplicated).
 * Falls back to `defaults` (filtered to `all`), then to every column, so the result is never empty
 * unless `all` is. Tolerant of null, malformed JSON and non-string entries.
 */
export function readColumns(raw: string | null, all: readonly string[], defaults: readonly string[]): string[] {
  let parsed: unknown;
  try {
    parsed = raw === null ? null : JSON.parse(raw);
  } catch {
    parsed = null;
  }
  if (Array.isArray(parsed)) {
    const keep = new Set<string>(parsed.filter((x): x is string => typeof x === 'string'));
    const stored = ordered(all, keep);
    if (stored.length > 0) return stored;
  }
  const fallback = ordered(all, new Set(defaults));
  return fallback.length > 0 ? fallback : [...all];
}

/** Toggles `key`, keeping `all`'s order. Unknown keys are ignored and the last visible column cannot be hidden. */
export function toggleColumn(visible: readonly string[], all: readonly string[], key: string): string[] {
  const current = ordered(all, new Set(visible));
  if (!all.includes(key)) return current;
  if (current.includes(key)) return current.length <= 1 ? current : current.filter((k) => k !== key);
  return ordered(all, new Set([...current, key]));
}
